import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import "./StudentForm.css";

const API_URL = process.env.REACT_APP_API_URL;

const NotAssignedStudents = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchNotAssignedStudents();
  }, []);

  const fetchNotAssignedStudents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students`);
      // Filter only students without day or hour
      const notAssigned = res.data.filter(
        (s) => !s.day || s.hour === null || s.hour === undefined
      );

      // Sort students: children by trainigGroup, adults by skillLevel
      const sorted = notAssigned.sort((a, b) => {
        // Adults first, then children
        if (a.adult && !b.adult) return -1;
        if (!a.adult && b.adult) return 1;

        if (a.adult && b.adult) {
          // Sort adults by skillLevel
          const skillOrder = ["Anfänger", "wenig Fortgeschritten", "Fortgeschritten", "gute:r Spieler:in"];
          const aIndex = skillOrder.indexOf(a.skillLevel || "");
          const bIndex = skillOrder.indexOf(b.skillLevel || "");
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        } else {
          // Sort children by trainigGroup
          const groupOrder = ["Kinderland", "Rot", "Orange", "Grün", "Gelb Hobby", "Gelb Team"];
          const aIndex = groupOrder.indexOf(a.trainigGroup || "");
          const bIndex = groupOrder.indexOf(b.trainigGroup || "");
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }
      });

      setStudents(sorted);

      // Select first student by default
      if (sorted.length > 0 && !selectedStudent) {
        setSelectedStudent(sorted[0]);
      }
    } catch (err) {
      console.error("Fehler beim Abrufen der Schüler:", err);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
  };

  // Helper function to get icon for children based on trainigGroup
  const getChildIcon = (student) => {
    if (student.adult) return ""; // Adults get no icon

    const groupColorMap = {
      Kinderland: "🩷",
      Rot: "🔴",
      Grün: "🟢",
      Orange: "🟠",
      "Gelb Team": "🟡",
      "Gelb Hobby": "🟡",
    };

    return groupColorMap[student.trainigGroup] || "⚪";
  };

  const formatAvailableTimes = (times) => {
    if (!Array.isArray(times) || times.length === 0) {
      return "Keine Zeiten verfügbar";
    }

    const dayOrder = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

    // Group by day
    const dayMap = {};
    times.forEach((time) => {
      const [day, hour] = time.split(" ");
      if (!dayMap[day]) {
        dayMap[day] = [];
      }
      dayMap[day].push(hour);
    });

    // Format output sorted by day order
    return dayOrder
      .filter((day) => dayMap[day]) // Only include days that have times
      .map((day) => {
        const sortedHours = dayMap[day].sort((a, b) => parseInt(a) - parseInt(b));
        return `${day}: ${sortedHours.join(", ")}`;
      })
      .join(" | ");
  };

  return (
    <div className="student-form-container">
      <div className="student-form-header">
        <h1>⚠️ Nicht zugewiesene Schüler</h1>
      </div>

      <div className="student-form-content has-selection">
        <div className="student-list-card">
          <h2>Schülerliste</h2>
          <p
            style={{ textAlign: "center", color: "#666", marginBottom: "15px" }}
          >
            <strong>{students.length}</strong> nicht zugewiesen
          </p>

          <div className="student-list">
            {students.map((student) => (
              <div
                className={`student-item ${
                  selectedStudent?._id === student._id ? "selected" : ""
                }`}
                key={student._id}
                onClick={() => handleSelectStudent(student)}
              >
                <span className="student-name">
                  {student.firstName} {student.lastName} {getChildIcon(student)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {selectedStudent && (
          <div className="edit-form-card">
            <h3 className="form-heading">Schüler-Details</h3>

            <div className="form-field">
              <label>Name</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.firstName} {selectedStudent.lastName} {getChildIcon(selectedStudent)}
              </div>
            </div>

            <div className="form-field">
              <label>Typ</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.adult ? "Erwachsenen-Training" : "Kinder-Training"}
              </div>
            </div>

            {selectedStudent.adult ? (
              <>
                <div className="form-field">
                  <label>Spielstärke</label>
                  <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                    {selectedStudent.skillLevel || "-"}
                  </div>
                </div>
                <div className="form-field">
                  <label>Geschlecht</label>
                  <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                    {selectedStudent.sex || "-"}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="form-field">
                  <label>Trainingsgruppe</label>
                  <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                    {getChildIcon(selectedStudent)} {selectedStudent.trainigGroup || "-"}
                  </div>
                </div>
                <div className="form-field">
                  <label>Teamspieler</label>
                  <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                    {selectedStudent.team ? "Ja" : "Nein"}
                  </div>
                </div>
              </>
            )}

            <div className="form-field">
              <label>Verfügbare Zeiten</label>
              <div style={{
                padding: "10px 14px",
                background: "#f8f9fa",
                borderRadius: "8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}>
                {formatAvailableTimes(selectedStudent.availableTimes)}
              </div>
            </div>

            <div className="form-field">
              <label>Trainigshäufigkeit</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.frequence || "-"} Mal pro Woche
              </div>
            </div>

            <div className="form-field">
              <label>Mitglied</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.member ? "Ja" : "Nein"}
              </div>
            </div>

            <div className="form-field">
              <label>Geburtsdatum</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.birthDate || "-"}
              </div>
            </div>

            <div className="form-field">
              <label>Email</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.email || "-"}
              </div>
            </div>

            <div className="form-field">
              <label>Telefon</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.phone || "-"}
              </div>
            </div>

            <div className="form-field">
              <label>Adresse</label>
              <div style={{ padding: "10px 14px", background: "#f8f9fa", borderRadius: "8px" }}>
                {selectedStudent.adress || "-"}
              </div>
            </div>

            <div className="form-field">
              <label>Notiz</label>
              <div style={{
                padding: "10px 14px",
                background: "#f8f9fa",
                borderRadius: "8px",
                minHeight: "60px"
              }}>
                {selectedStudent.comment || "-"}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotAssignedStudents;
